import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { Card } from '../components/Card.tsx';
import { PortalFooter } from '../components/PortalFooter.tsx';
import { PortalHealthBanner } from '../components/PortalHealthBanner.tsx';
import { PortalNavBar } from '../components/PortalNavBar.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface PortalContact {
  id: string;
  organizationId: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
}

interface PerTicketRow {
  ticketId: string;
  displayNumber: number;
  subject: string;
  clientTicketNumber: string | null;
  endClient: string | null;
  urgency: string | null;
  requesterName: string | null;
  type: string;
  status: string;
  createdAt: string;
  solvedAt: string | null;
  totalMinutes: number;
  billableMinutes: number;
}

interface PeriodSummary {
  id: string;
  periodStart: string;
  periodEnd: string;
  grantedHours: number;
  consumedHours: number;
  remainingHours: number;
  rolloverInHours?: number;
  rolloverInRemaining?: number;
  rolloverOutHours?: number | null;
  forfeitedHours?: number | null;
  status?: 'open' | 'closed';
  isFinal?: boolean;
}

interface BillingResponse {
  period: PeriodSummary | null;
  perTicket: PerTicketRow[];
  monthKey: string | null;
}

interface ProjectRollup {
  id: string;
  name: string;
  billingMode: 'flat_fee' | 'block_hours';
  status: 'active' | 'completed' | 'cancelled';
  budgetedHours: number;
  consumedHours: number;
  subtaskTotal: number;
  subtaskCompleted: number;
  dueDate: string | null;
}

interface ProjectsResponse {
  projects: ProjectRollup[];
  monthKey: string | null;
}

interface HistoryPeriod {
  periodId: string;
  periodStart: string;
  periodEnd: string;
  monthKey: string;
  status: 'open' | 'closed';
  isFinal: boolean;
  baseHours: number;
  adHocHours: number;
  rolloverInHours: number;
  consumedHours: number;
  rolloverOutHours: number | null;
  forfeitedHours: number | null;
}

interface HistoryResponse {
  periods: HistoryPeriod[];
}

interface ProjectMonthly {
  monthKey: string;
  monthStart: string;
  totalHours: number;
  billableHours: number;
}

interface ProjectMonthlyResponse {
  project: { id: string; name: string; billingMode: string; budgetedHours: number };
  months: ProjectMonthly[];
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function currentMonthKey(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key: string): string {
  const [y, m] = key.split('-');
  if (!y || !m) return key;
  const idx = Number(m) - 1;
  if (idx < 0 || idx > 11) return key;
  return `${MONTH_NAMES[idx]} ${y}`;
}

function buildMonthOptions(): string[] {
  const keys: string[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    keys.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return keys;
}

function daysLeftInMonth(): number {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const ms = end.getTime() - now.getTime();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export function PortalReportsPage(): React.ReactElement {
  useDocumentTitle('Reports');
  const navigate = useNavigate();
  const monthOptions = useMemo(buildMonthOptions, []);
  const [contact, setContact] = useState<PortalContact | null>(null);
  const [contactLoaded, setContactLoaded] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonthKey());
  const [period, setPeriod] = useState<PeriodSummary | null>(null);
  const [tickets, setTickets] = useState<PerTicketRow[] | null>(null);
  const [projects, setProjects] = useState<ProjectRollup[] | null>(null);
  const [history, setHistory] = useState<HistoryPeriod[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ contact: PortalContact | null }>('/api/portal/auth/me')
      .then((r) => {
        if (!r.contact) {
          navigate('/login', { replace: true, state: { from: '/portal/reports' } });
          return;
        }
        setContact(r.contact);
      })
      .catch(() => {
        navigate('/login', { replace: true, state: { from: '/portal/reports' } });
      })
      .finally(() => setContactLoaded(true));
  }, [navigate]);

  useEffect(() => {
    if (!contact) return;
    apiFetch<HistoryResponse>('/api/portal/tickets/_meta/billing-history?months=12')
      .then((r) => setHistory(r.periods))
      .catch(() => setHistory([]));
  }, [contact]);

  useEffect(() => {
    if (!contact) return;
    let cancelled = false;
    setError(null);
    const monthQuery = selectedMonth === 'all' ? '' : `?month=${selectedMonth}`;
    Promise.all([
      apiFetch<BillingResponse>(`/api/portal/tickets/_meta/billing-summary${monthQuery}`),
      apiFetch<ProjectsResponse>(`/api/portal/tickets/_meta/projects-summary${monthQuery}`),
    ])
      .then(([b, p]) => {
        if (cancelled) return;
        setPeriod(b.period);
        setTickets(b.perTicket);
        setProjects(p.projects);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load report data.');
      });
    return () => {
      cancelled = true;
    };
  }, [contact, selectedMonth]);

  const monthQuery = selectedMonth === 'all' ? '' : `?month=${selectedMonth}`;
  const isAllTime = selectedMonth === 'all';
  const scopeLabel = isAllTime ? 'all time' : monthLabel(selectedMonth);

  if (!contactLoaded) {
    return (
      <div className="bg-white">
        <PortalHealthBanner />
        <PortalNavBar contact={null} active="reports" />
        <main className="mx-auto max-w-6xl px-6 py-10 sm:px-8">
          <p className="text-[var(--color-muted)]">Loading…</p>
        </main>
      </div>
    );
  }

  if (!contact) return <></>;

  const totalBillable = (tickets ?? []).reduce((s, r) => s + r.billableMinutes, 0);
  const totalAll = (tickets ?? []).reduce((s, r) => s + r.totalMinutes, 0);

  return (
    <div className="bg-white">
      <PortalHealthBanner />
      <PortalNavBar contact={contact} active="reports" />

      <main className="mx-auto max-w-6xl space-y-6 px-6 py-10 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <Link to="/portal" className="text-sm text-[var(--color-muted)] hover:underline">
              ← Help center
            </Link>
            <h1 className="mt-1 font-display text-3xl font-bold text-[var(--color-header)]">
              Hours &amp; Reports
            </h1>
            <p className="mt-1 text-sm text-[var(--color-muted)]">
              Block-hour balance plus monthly, per-ticket, and per-project totals. Billing month
              runs 1st to 1st.
            </p>
          </div>
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-[var(--color-muted)]">Scope</span>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="mt-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
            >
              {monthOptions.map((k, i) => (
                <option key={k} value={k}>
                  {i === 0 ? `Current month — ${monthLabel(k)}` : monthLabel(k)}
                </option>
              ))}
              <option value="all">All time</option>
            </select>
          </label>
        </div>

        {error && (
          <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
            <p className="text-sm text-[var(--color-danger)]">{error}</p>
          </Card>
        )}

        <PeriodPanel
          period={period}
          isAllTime={isAllTime}
          isCurrentMonth={selectedMonth === currentMonthKey()}
          scopeLabel={scopeLabel}
        />

        <MonthlyTrend history={history} />

        <PerTicketSection
          tickets={tickets}
          totalAll={totalAll}
          totalBillable={totalBillable}
          scopeLabel={scopeLabel}
          monthQuery={monthQuery}
        />

        <PerProjectSection
          projects={projects}
          scopeLabel={scopeLabel}
          monthQuery={monthQuery}
        />
      </main>

      <PortalFooter />
    </div>
  );
}

function MonthlyTrend({ history }: { history: HistoryPeriod[] | null }): React.ReactElement {
  if (history === null) {
    return (
      <Card>
        <p className="text-sm text-[var(--color-muted)]">Loading monthly trend…</p>
      </Card>
    );
  }
  if (history.length === 0) {
    return (
      <Card>
        <p className="text-sm text-[var(--color-muted)]">
          No completed billing periods yet. Once a month closes, your monthly trend will appear here.
        </p>
      </Card>
    );
  }
  const ascending = [...history].reverse();
  const max = Math.max(...ascending.map((p) => p.consumedHours), 1);
  const totalAll = ascending.reduce((s, p) => s + p.consumedHours, 0);
  const avg = totalAll / ascending.length;

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-base font-semibold text-[var(--color-header)]">Monthly hours used</h2>
        <div className="flex items-center gap-3 text-xs text-[var(--color-muted)]">
          <span>Avg {avg.toFixed(2)}h / mo</span>
          <CsvLink href="/api/portal/tickets/_meta/billing-history.csv?months=12">
            Download CSV
          </CsvLink>
        </div>
      </div>
      <Card>
        <div className="flex items-end gap-2 overflow-x-auto pb-1" style={{ minHeight: '180px' }}>
          {ascending.map((p) => {
            const heightPct = (p.consumedHours / max) * 100;
            return (
              <div
                key={p.periodId}
                className="flex min-w-[44px] flex-1 flex-col items-center gap-1"
                title={`${monthLabel(p.monthKey)}: ${p.consumedHours.toFixed(2)}h used`}
              >
                <div className="font-mono text-[10px] text-[var(--color-muted)]">
                  {p.consumedHours.toFixed(1)}
                </div>
                <div className="flex h-32 w-full items-end">
                  <div
                    className="w-full rounded-t bg-gradient-to-t from-[var(--color-t3-purple)] to-[var(--color-t3-blue)]"
                    style={{ height: `${Math.max(2, heightPct)}%` }}
                  />
                </div>
                <div className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">
                  {p.monthKey.slice(5)}/{p.monthKey.slice(2, 4)}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function PerTicketSection({
  tickets,
  totalAll,
  totalBillable,
  scopeLabel,
  monthQuery,
}: {
  tickets: PerTicketRow[] | null;
  totalAll: number;
  totalBillable: number;
  scopeLabel: string;
  monthQuery: string;
}): React.ReactElement {
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-base font-semibold text-[var(--color-header)]">
          Hours per ticket — {scopeLabel}
        </h2>
        <div className="flex items-center gap-3 text-xs text-[var(--color-muted)]">
          <span>
            Total: {(totalAll / 60).toFixed(2)}h ({(totalBillable / 60).toFixed(2)}h billable)
          </span>
          <CsvLink href={`/api/portal/tickets/_meta/billing-summary.csv${monthQuery}`}>
            Download CSV
          </CsvLink>
          <CsvLink href={`/api/portal/tickets/_meta/time-entries.csv${monthQuery}`}>
            Every time entry (CSV)
          </CsvLink>
        </div>
      </div>
      {tickets === null ? (
        <p className="text-sm text-[var(--color-muted)]">Loading…</p>
      ) : tickets.length === 0 ? (
        <Card>
          <p className="text-center text-sm text-[var(--color-muted)]">
            No tickets with logged time in {scopeLabel}.
          </p>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
              <tr>
                <th className="px-3 py-3">#</th>
                <th className="px-3 py-3">Your ticket #</th>
                <th className="px-3 py-3">End client</th>
                <th className="px-3 py-3">Subject</th>
                <th className="px-3 py-3">Requested by</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3 text-right">Total</th>
                <th className="px-3 py-3 text-right">Billable</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((r) => (
                <tr key={r.ticketId} className="border-t border-[var(--color-border)] align-top">
                  <td className="whitespace-nowrap px-3 py-3 font-mono text-xs">T-{r.displayNumber}</td>
                  <td className="whitespace-nowrap px-3 py-3 font-mono text-xs">
                    {r.clientTicketNumber ?? <span className="text-[var(--color-muted)]">—</span>}
                  </td>
                  <td className="px-3 py-3">
                    {r.endClient ?? <span className="text-[var(--color-muted)]">—</span>}
                  </td>
                  <td className="px-3 py-3">
                    <Link to={`/portal/tickets/${r.ticketId}`} className="text-[var(--color-t3-blue)] hover:underline">
                      {r.subject}
                    </Link>
                    <div className="mt-0.5 text-xs text-[var(--color-muted)]">
                      <span className="uppercase tracking-wide">{r.type}</span>
                      {r.urgency && <> · {r.urgency} urgency</>}
                      {' · '}opened {r.createdAt.slice(0, 10)}
                      {r.solvedAt && <> · solved {r.solvedAt.slice(0, 10)}</>}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs">
                    {r.requesterName ?? <span className="text-[var(--color-muted)]">—</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs uppercase tracking-wide text-[var(--color-muted)]">
                    {r.status.replace(/_/g, ' ')}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-mono">{(r.totalMinutes / 60).toFixed(2)}h</td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-mono">{(r.billableMinutes / 60).toFixed(2)}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PerProjectSection({
  projects,
  scopeLabel,
  monthQuery,
}: {
  projects: ProjectRollup[] | null;
  scopeLabel: string;
  monthQuery: string;
}): React.ReactElement {
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);
  const [drilldown, setDrilldown] = useState<ProjectMonthlyResponse | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  function toggle(projectId: string): void {
    if (openProjectId === projectId) {
      setOpenProjectId(null);
      setDrilldown(null);
      return;
    }
    setOpenProjectId(projectId);
    setDrilldown(null);
    setDrilldownLoading(true);
    apiFetch<ProjectMonthlyResponse>(
      `/api/portal/tickets/_meta/projects/${projectId}/monthly?months=12`,
    )
      .then((r) => setDrilldown(r))
      .catch(() => setDrilldown(null))
      .finally(() => setDrilldownLoading(false));
  }

  if (projects === null) {
    return <p className="text-sm text-[var(--color-muted)]">Loading projects…</p>;
  }
  if (projects.length === 0) {
    return (
      <Card>
        <p className="text-center text-sm text-[var(--color-muted)]">No projects on file.</p>
      </Card>
    );
  }

  const blockHours = projects.filter((p) => p.billingMode === 'block_hours');
  const flatFee = projects.filter((p) => p.billingMode === 'flat_fee');

  return (
    <div className="space-y-6">
      {blockHours.length > 0 && (
        <div>
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="text-base font-semibold text-[var(--color-header)]">
              Projects · block-hours — {scopeLabel}
            </h2>
            <div className="flex items-center gap-3 text-xs text-[var(--color-muted)]">
              <span>Click a project for monthly history</span>
              <CsvLink href={`/api/portal/tickets/_meta/projects-summary.csv${monthQuery}`}>
                Download CSV
              </CsvLink>
            </div>
          </div>
          <ProjectTable
            projects={blockHours}
            kind="block"
            openProjectId={openProjectId}
            drilldown={drilldown}
            drilldownLoading={drilldownLoading}
            onToggle={toggle}
          />
        </div>
      )}

      {flatFee.length > 0 && (
        <div>
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="text-base font-semibold text-[var(--color-header)]">
              Projects · fixed fee — {scopeLabel}
            </h2>
            <span className="text-xs text-[var(--color-muted)]">
              Progress is based on completed tasks
            </span>
          </div>
          <ProjectTable
            projects={flatFee}
            kind="flat"
            openProjectId={openProjectId}
            drilldown={drilldown}
            drilldownLoading={drilldownLoading}
            onToggle={toggle}
          />
        </div>
      )}
    </div>
  );
}

function ProjectTable({
  projects,
  kind,
  openProjectId,
  drilldown,
  drilldownLoading,
  onToggle,
}: {
  projects: ProjectRollup[];
  kind: 'block' | 'flat';
  openProjectId: string | null;
  drilldown: ProjectMonthlyResponse | null;
  drilldownLoading: boolean;
  onToggle: (id: string) => void;
}): React.ReactElement {
  return (
    <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
      <table className="w-full text-sm">
        <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
          <tr>
            <th className="px-4 py-3">Project</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Tasks</th>
            {kind === 'block' && <th className="px-4 py-3">Due</th>}
            {kind === 'block' && <th className="px-4 py-3 text-right">Hours used</th>}
            {kind === 'flat' && <th className="px-4 py-3 text-right">% complete</th>}
            {kind === 'flat' && <th className="px-4 py-3">Progress</th>}
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => {
            const open = openProjectId === p.id;
            const pct =
              p.subtaskTotal > 0
                ? Math.round((p.subtaskCompleted / p.subtaskTotal) * 100)
                : p.status === 'completed'
                  ? 100
                  : 0;
            return (
              <Fragment key={p.id}>
                <tr
                  className="cursor-pointer border-t border-[var(--color-border)] hover:bg-[var(--color-surface)]"
                  onClick={() => onToggle(p.id)}
                >
                  <td className="px-4 py-3 font-medium">
                    <span className="mr-2 text-xs text-[var(--color-muted)]">{open ? '▾' : '▸'}</span>
                    {p.name}
                  </td>
                  <td className="px-4 py-3 text-xs uppercase tracking-wide text-[var(--color-muted)]">
                    {p.status}
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--color-muted)]">
                    {p.subtaskTotal > 0 ? `${p.subtaskCompleted}/${p.subtaskTotal}` : '—'}
                  </td>
                  {kind === 'block' && (
                    <td className="px-4 py-3 text-xs text-[var(--color-muted)]">
                      {p.dueDate ?? '—'}
                    </td>
                  )}
                  {kind === 'block' && (
                    <td className="px-4 py-3 text-right font-mono">{p.consumedHours.toFixed(2)}h</td>
                  )}
                  {kind === 'flat' && (
                    <td className="px-4 py-3 text-right font-mono">{pct}%</td>
                  )}
                  {kind === 'flat' && (
                    <td className="px-4 py-3">
                      <div className="h-1.5 w-32 overflow-hidden rounded-full bg-[var(--color-border)]">
                        <div
                          className="h-full bg-[var(--color-t3-blue)]"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </td>
                  )}
                </tr>
                {open && (
                  <tr className="border-t border-[var(--color-border)] bg-[var(--color-surface)]/40">
                    <td colSpan={5} className="px-4 py-3">
                      <ProjectMonthlyDetail
                        loading={drilldownLoading}
                        data={drilldown && drilldown.project.id === p.id ? drilldown : null}
                        budgeted={p.budgetedHours}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ProjectMonthlyDetail({
  loading,
  data,
  budgeted,
}: {
  loading: boolean;
  data: ProjectMonthlyResponse | null;
  budgeted: number;
}): React.ReactElement {
  if (loading) return <p className="text-xs text-[var(--color-muted)]">Loading monthly detail…</p>;
  if (!data) return <p className="text-xs text-[var(--color-muted)]">No detail available.</p>;

  const max = Math.max(...data.months.map((m) => m.totalHours), 1);
  const total = data.months.reduce((s, m) => s + m.totalHours, 0);
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between text-xs">
        <span className="text-[var(--color-muted)]">Last 12 months</span>
        <span className="font-mono">
          {total.toFixed(2)}h logged{' '}
          {budgeted > 0 ? `· budget ${budgeted.toFixed(2)}h` : ''}
        </span>
      </div>
      <div className="space-y-1">
        {data.months.map((m) => {
          const pct = (m.totalHours / max) * 100;
          return (
            <div key={m.monthKey} className="grid grid-cols-12 items-center gap-2 text-xs">
              <div className="col-span-3 text-[var(--color-muted)]">{monthLabel(m.monthKey)}</div>
              <div className="col-span-7 h-2 overflow-hidden rounded-full bg-[var(--color-border)]">
                <div
                  className="h-full bg-[var(--color-t3-purple)]"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="col-span-2 text-right font-mono text-[var(--color-muted)]">
                {m.totalHours.toFixed(2)}h
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CsvLink({ href, children }: { href: string; children: React.ReactNode }): React.ReactElement {
  return (
    <a
      href={href}
      className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2.5 py-1 text-xs text-[var(--color-header)] hover:bg-[var(--color-surface)]"
    >
      {children}
    </a>
  );
}

function PeriodPanel({
  period,
  isAllTime,
  isCurrentMonth,
  scopeLabel,
}: {
  period: PeriodSummary | null;
  isAllTime: boolean;
  isCurrentMonth: boolean;
  scopeLabel: string;
}): React.ReactElement | null {
  if (isAllTime) return null;
  if (!period) {
    return (
      <Card>
        <p className="text-center text-sm text-[var(--color-muted)]">
          No block-hour subscription was active in {scopeLabel}.
        </p>
      </Card>
    );
  }

  // Defensive: fall back to 0 for any field the API didn't populate, so a
  // backend/contract drift surfaces as zeroed-out stats rather than a
  // crash that blanks the whole page.
  const granted = period.grantedHours ?? 0;
  const consumed = period.consumedHours ?? 0;
  const remaining = period.remainingHours ?? granted - consumed;
  const usedPct =
    granted > 0
      ? Math.min(100, (consumed / granted) * 100)
      : 0;
  const overUsed = remaining <= 0;
  const rolloverAtRisk = isCurrentMonth ? Math.max(0, period.rolloverInRemaining ?? 0) : 0;
  const daysLeft = isCurrentMonth ? daysLeftInMonth() : null;

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <BigStat
          label={isCurrentMonth ? 'Granted this month' : 'Granted'}
          value={`${granted.toFixed(1)}h`}
          sub={`${period.periodStart} – ${period.periodEnd}`}
        />
        <BigStat
          label="Used"
          value={`${consumed.toFixed(1)}h`}
          sub={`${Math.round(usedPct)}% of granted`}
        />
        <BigStat
          label="Remaining"
          value={`${remaining.toFixed(1)}h`}
          sub={
            overUsed
              ? 'over allotment'
              : daysLeft !== null
                ? `${daysLeft} day${daysLeft === 1 ? '' : 's'} left in month`
                : period.status === 'closed'
                  ? 'period closed'
                  : 'available'
          }
          danger={overUsed}
        />
      </div>

      <Card>
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-[var(--color-muted)]">Period progress — {scopeLabel}</span>
          <span className="font-mono">
            {consumed.toFixed(2)}h of {granted.toFixed(2)}h
          </span>
        </div>
        <div className="mt-2 h-3 overflow-hidden rounded-full bg-[var(--color-border)]">
          <div
            className={
              'h-full ' +
              (overUsed
                ? 'bg-[var(--color-danger)]'
                : usedPct >= 80
                  ? 'bg-amber-500'
                  : 'bg-[var(--color-t3-blue)]')
            }
            style={{ width: `${usedPct}%` }}
          />
        </div>
        {overUsed && isCurrentMonth && (
          <p className="mt-3 text-xs text-[var(--color-danger)]">
            You've used more than this month's allotment. Additional time will be settled at
            month-end — your billing contact will be notified.
          </p>
        )}
        {period.status === 'closed' && (
          <div className="mt-3 grid gap-2 border-t border-[var(--color-border)] pt-3 text-xs sm:grid-cols-3">
            <KV label="Rolled in" value={fmtH(period.rolloverInHours)} />
            <KV label="Rolled out" value={fmtH(period.rolloverOutHours)} />
            <KV
              label="Forfeited"
              value={fmtH(period.forfeitedHours)}
              danger={(period.forfeitedHours ?? 0) > 0}
            />
          </div>
        )}
      </Card>

      {rolloverAtRisk > 0 && (
        <Card className="border-amber-300 bg-amber-50/40">
          <div className="flex items-baseline justify-between">
            <h2 className="text-base font-semibold text-amber-900">Rollover at risk</h2>
            <span className="font-mono text-sm text-amber-900">{rolloverAtRisk.toFixed(2)}h</span>
          </div>
          <p className="mt-2 text-xs text-amber-900">
            These hours rolled in from last month and expire on {period.periodEnd}. They're
            consumed first, so any work logged this month draws from rollover before this month's
            allotment.
          </p>
        </Card>
      )}
    </>
  );
}

function BigStat({
  label,
  value,
  sub,
  danger,
}: {
  label: string;
  value: string;
  sub?: string;
  danger?: boolean;
}): React.ReactElement {
  return (
    <Card className={danger ? 'border-[var(--color-danger)]/40' : ''}>
      <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">{label}</div>
      <div
        className={
          'mt-1 font-display text-4xl font-bold ' +
          (danger ? 'text-[var(--color-danger)]' : 'text-[var(--color-header)]')
        }
      >
        {value}
      </div>
      {sub && <div className="mt-0.5 text-xs text-[var(--color-muted)]">{sub}</div>}
    </Card>
  );
}

function KV({
  label,
  value,
  danger,
}: {
  label: string;
  value: string;
  danger?: boolean;
}): React.ReactElement {
  return (
    <div>
      <div className="text-[var(--color-muted)]">{label}</div>
      <div className={'font-mono ' + (danger ? 'text-[var(--color-danger)]' : 'text-[var(--color-header)]')}>
        {value}
      </div>
    </div>
  );
}

function fmtH(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(2)}h`;
}
